import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatSpread, coverTeam, MODE_LABEL } from "@/lib/teams";
import Avatar from "@/components/Avatar";
import { Loader2, ChevronDown, Lock, Trophy, Crown } from "lucide-react";

function Chip({ status, dim, small, title }) {
  const map = {
    W: "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/40",
    L: "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40",
    P: "bg-white/5 text-white/40 border-white/10",
    "-": "bg-transparent text-white/20 border-white/5",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center justify-center ${small ? "w-5 h-5 text-[10px]" : "w-6 h-6 text-[11px]"} font-bold border ${map[status]} ${dim ? "opacity-30" : ""}`}
    >
      {status === "P" ? "·" : status}
    </span>
  );
}

// combo mode: SU chip | ATS chip (ATS lit only when the player took the spread)
function ComboCell({ su, ats, took }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      <Chip status={su} small title="Straight up" />
      <Chip status={ats} small dim={!took} title={took ? "Spread pick" : "Spread not taken"} />
    </span>
  );
}

function Badges({ score }) {
  return (
    <span className="inline-flex items-center gap-1 ml-2 align-middle">
      {score.week_winner && (
        <span className="inline-flex items-center gap-0.5 text-[9px] bg-[#EAB308]/15 text-[#EAB308] border border-[#EAB308]/40 px-1 py-0.5 uppercase tracking-wider" title="Week Winner bonus">
          <Crown className="w-2.5 h-2.5" /> +{score.week_winner_bonus}
        </span>
      )}
      {score.covers > 0 && (
        <span className="text-[9px] bg-[#EAB308]/15 text-[#EAB308] border border-[#EAB308]/40 px-1 py-0.5 uppercase tracking-wider" title="Cover bonus">
          CV×{score.covers} +{score.cover_bonus}
        </span>
      )}
      {score.lone_wolf_count > 0 && (
        <span className="text-[9px] bg-[#E31837]/15 text-[#E31837] border border-[#E31837]/40 px-1 py-0.5 uppercase tracking-wider" title="Lone Wolf bonus">
          LW×{score.lone_wolf_count} +{score.lone_wolf}
        </span>
      )}
    </span>
  );
}

export default function Results() {
  const [weeks, setWeeks] = useState([]);
  const [weekNum, setWeekNum] = useState(null);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/weeks").then((res) => {
      setWeeks(res.data);
      const withResults = [...res.data].reverse().find((w) => w.decided > 0);
      setWeekNum(withResults ? withResults.week_number : res.data[0]?.week_number);
    });
  }, []);

  useEffect(() => {
    if (!weekNum) return;
    let alive = true;
    setLoading(true);
    const load = () =>
      api.get(`/results/${weekNum}`).then((res) => { if (alive) setData(res.data); }).finally(() => alive && setLoading(false));
    load();
    const t = setInterval(load, 20000); // live refresh
    return () => { alive = false; clearInterval(t); };
  }, [weekNum]);

  const mode = settings?.pick_mode || "straight_up";
  const atsMode = mode === "ats";
  const combo = mode === "combo";
  const showLines = atsMode || combo;

  // team a player needed to pick: cover team in ATS (PUSH = void), else straight-up winner
  const targetTeam = (g) => {
    if (!g.winner) return null;
    if (atsMode && g.spread !== null && g.spread !== undefined && g.home_score != null && g.away_score != null) {
      const adj = g.home_score + g.spread;
      if (adj > g.away_score) return g.home;
      if (adj < g.away_score) return g.away;
      return "PUSH";
    }
    return g.winner;
  };

  const cell = (row, g) => {
    if (!data.locked) return row.submitted ? "P" : "-";
    const pick = row.selections[g.id];
    const t = targetTeam(g);
    if (!t) return pick ? "P" : "-";
    if (!pick) return "-";
    if (t === "PUSH") return "P";
    return pick === t ? "W" : "L";
  };

  // combo mode: [straight-up status, spread status, took spread]
  const comboCell = (row, g) => {
    if (!data.locked) return row.submitted ? ["P", "P", false] : ["-", "-", false];
    const pick = row.selections[g.id];
    const took = (row.spread_picks || []).includes(g.id);
    if (!pick) return ["-", "-", took];
    if (!g.winner) return ["P", "P", took];
    const su = pick === g.winner ? "W" : "L";
    const c = coverTeam(g);
    const ats = !c ? "-" : c === "PUSH" ? "P" : pick === c ? "W" : "L";
    return [su, ats, took];
  };

  const renderCell = (row, g) => {
    if (!combo) return <Chip status={cell(row, g)} />;
    const [su, ats, took] = comboCell(row, g);
    return <ComboCell su={su} ats={ats} took={took} />;
  };

  const gameScore = (g) =>
    g.away_score !== null && g.away_score !== undefined ? `${g.away_score}-${g.home_score}` : null;

  return (
    <div className="max-w-full mx-auto px-4 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4 max-w-7xl mx-auto">
        <div>
          <h1 className="font-['Bebas_Neue'] text-5xl tracking-tight leading-none flex items-center gap-3">
            WEEKLY RESULTS
            {data && data.locked && (
              <span className={`text-xs font-sans uppercase tracking-widest px-2 py-1 border ${data.finalized ? "text-[#22C55E] border-[#22C55E]/40" : "text-[#EAB308] border-[#EAB308]/40"}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${data.finalized ? "bg-[#22C55E]" : "bg-[#EAB308] live-dot"}`} />
                {data.finalized ? "Final" : "Live"}
              </span>
            )}
          </h1>
          <p className="text-white/50 text-sm mt-1">Every pick graded live. Totals include bonuses.</p>
        </div>
        <div className="relative">
          <select
            value={weekNum || ""}
            onChange={(e) => setWeekNum(Number(e.target.value))}
            className="appearance-none bg-[#141414] border border-white/10 pl-4 pr-10 py-2.5 rounded-sm text-sm uppercase tracking-wider focus:ring-2 focus:ring-[#E31837] focus:outline-none"
            data-testid="results-week-selector"
          >
            {weeks.map((w) => (
              <option key={w.week_number} value={w.week_number}>Week {w.week_number}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 top-3 pointer-events-none text-white/50" />
        </div>
      </div>

      {settings && (
        <div className="max-w-7xl mx-auto mb-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/40 uppercase tracking-wider" data-testid="scoring-legend">
          <span className={showLines ? "text-[#EAB308]" : "text-white/60"}>Mode: {MODE_LABEL[mode]}</span>
          <span>Correct = slot pts ({settings.points_thu_mon} / {settings.points_sun_early} / {settings.points_snf})</span>
          {combo && <span className="text-[#EAB308]">Cover = +{settings.cover_bonus}</span>}
          {combo && <span className="text-white/60">Chips: SU | ATS</span>}
          <span className="text-[#EF4444]">Wrong = −{settings.wrong_pick_penalty}</span>
          <span className="text-[#E31837]">Lone Wolf = +{settings.lone_wolf_bonus}</span>
          <span className="text-[#EAB308]">Week Winner = +{settings.week_winner_bonus}</span>
        </div>
      )}

      {loading || !data ? (
        <div className="flex items-center gap-2 text-white/50 py-20 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading results…
        </div>
      ) : !data.locked ? (
        <div className="max-w-7xl mx-auto bg-[#141414] border border-white/10 px-6 py-16 text-center" data-testid="results-hidden">
          <Lock className="w-8 h-8 text-[#EAB308] mx-auto mb-3" />
          <p className="font-['Bebas_Neue'] text-3xl tracking-tight">RESULTS HIDDEN</p>
          <p className="text-white/50 text-sm mt-1">
            Picks for Week {weekNum} unlock after the cutoff. Check back once games kick off.
          </p>
        </div>
      ) : (
        <>
        {/* Top 5 this week */}
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="results-top5">
          {data.rows.slice(0, 5).map((row, i) => (
            <div key={row.user_id} className={`bg-[#141414] border p-4 flex flex-col gap-2 ${i === 0 ? "border-[#EAB308]/50" : "border-white/10"}`} data-testid={`results-top5-${i + 1}`}>
              <div className="flex items-center justify-between">
                {i === 0 ? <Crown className="w-6 h-6 text-[#EAB308]" /> : <span className="font-['Bebas_Neue'] text-xl text-white/40">#{i + 1}</span>}
                <Badges score={row.score} />
              </div>
              <div className="font-['Bebas_Neue'] text-xl tracking-wide leading-none truncate">{row.username}</div>
              <div className={`font-['Bebas_Neue'] text-3xl tracking-wide leading-none ${row.score.total > 0 ? "text-[#22C55E]" : row.score.total < 0 ? "text-[#EF4444]" : "text-white/50"}`}>
                {row.score.total}
              </div>
            </div>
          ))}
        </div>
        {/* Desktop / tablet: full matrix */}
        <div className="hidden md:block max-w-7xl mx-auto border border-white/10 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[#0A0A0A]">
                <th className="sticky left-0 z-10 bg-[#0A0A0A] text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-white/50 border-b border-white/10 min-w-[160px]">
                  Player
                </th>
                {data.week.games.map((g) => {
                  const sc = gameScore(g);
                  const line = showLines ? formatSpread(g) : null;
                  return (
                    <th key={g.id} className="px-1 py-2 border-b border-white/10 border-l border-white/5 min-w-[54px]" title={`${g.away} @ ${g.home}${line ? ` (${line})` : ""}`}>
                      <div className="text-[10px] font-bold leading-tight">{g.away}</div>
                      <div className="text-[9px] text-white/40 leading-tight">@{g.home}</div>
                      {line && <div className="text-[8px] text-[#EAB308] leading-tight">{line}</div>}
                      {sc ? (
                        <div className={`text-[9px] tabular-nums ${g.status === "final" ? "text-[#22C55E]" : "text-[#EAB308]"}`}>{sc}</div>
                      ) : (
                        <div className="text-[9px] text-[#E31837]">{g.points}</div>
                      )}
                    </th>
                  );
                })}
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-[0.2em] text-white/70 border-b border-white/10 border-l border-white/10 bg-[#141414]">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={row.user_id} className="hover:bg-white/5 transition-colors duration-150" data-testid={`result-row-${row.username}`}>
                  <td className="sticky left-0 z-10 bg-[#0A0A0A] px-4 py-2 border-b border-white/10 font-medium whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <span className="text-white/30 text-xs">{i + 1}</span>
                      {i === 0 && <Trophy className="w-3.5 h-3.5 text-[#EAB308]" />}
                      <Avatar team={row.avatar} username={row.username} size="sm" />
                      {row.username}
                      {!row.submitted && <span className="text-[10px] text-white/30">no picks</span>}
                      <Badges score={row.score} />
                    </span>
                  </td>
                  {data.week.games.map((g, gi) => (
                    <td key={g.id} className="text-center py-2 px-1 border-b border-white/10 border-l border-white/5" data-testid={`result-cell-${row.username}-${gi}`}>
                      {renderCell(row, g)}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right border-b border-white/10 border-l border-white/10 bg-[#141414]">
                    <span className={`font-['Bebas_Neue'] text-2xl tracking-wide ${row.score.total > 0 ? "text-[#22C55E]" : row.score.total < 0 ? "text-[#EF4444]" : "text-white/50"}`}>
                      {row.score.total}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked player cards */}
        <div className="md:hidden space-y-3" data-testid="results-mobile">
          {data.rows.map((row, i) => (
            <div key={row.user_id} className="bg-[#141414] border border-white/10" data-testid={`result-card-${row.username}`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="flex items-center gap-2 font-medium flex-wrap">
                  <span className="text-white/30 text-xs">{i + 1}</span>
                  {i === 0 && <Trophy className="w-4 h-4 text-[#EAB308]" />}
                  <Avatar team={row.avatar} username={row.username} size="sm" />
                  {row.username}
                  <Badges score={row.score} />
                </div>
                <span className={`font-['Bebas_Neue'] text-3xl tracking-wide leading-none ${row.score.total > 0 ? "text-[#22C55E]" : row.score.total < 0 ? "text-[#EF4444]" : "text-white/50"}`}>
                  {row.score.total}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 p-3">
                {data.week.games.map((g) => {
                  const line = showLines ? formatSpread(g) : null;
                  return (
                    <div key={g.id} className="flex flex-col items-center gap-1 min-w-[44px]" data-testid={`result-mobile-${row.username}-${g.id}`}>
                      <span className="text-[9px] text-white/40 leading-none">{g.away}/{g.home}</span>
                      {line && <span className="text-[8px] text-[#EAB308] leading-none" data-testid="mobile-spread">{line}</span>}
                      {gameScore(g) ? (
                        <span className={`text-[8px] tabular-nums leading-none ${g.status === "final" ? "text-[#22C55E]" : "text-[#EAB308]"}`}>{gameScore(g)}</span>
                      ) : (
                        <span className="text-[8px] text-[#E31837] leading-none">{g.points}</span>
                      )}
                      {renderCell(row, g)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
